import { ThemeToggle } from '@/components/common/ThemeToggle';
import Button from '@/components/global/button';
import { NotificationDropdown } from './notification-dropdown';
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from '@/components/ui/menubar';
import ROUTES from '@/lib/constants/routeConfig';
import { useUser } from '@/lib/hooks/useUser';
import { userService } from '@/lib/services/user.service';
import {
  I18nProviderClient,
  useChangeLocale,
  useCurrentLocale,
  useI18n,
} from '@/locales/client';
import { ChatboxColors, Crisp } from 'crisp-sdk-web';
import {
  ArrowLeftRight,
  Globe,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Settings,
  UserIcon,
} from 'lucide-react';
import { signOut, useSession } from 'next-auth/react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

function ControlsContent() {
  const currentLocale = useCurrentLocale();
  const changeLocale = useChangeLocale();
  const { status, data: session, update } = useSession();
  const { isAdmin, isLawyer } = useUser();
  const t = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const router = useRouter();

  const handleLanguageChange = () => {
    setIsPending(true);
    changeLocale(currentLocale === 'en' ? 'fr' : 'en');
    setIsPending(false);
  };

  const handleLogout = () => {
    setIsLoading(true);
    signOut();
    setIsLoading(false);
  };

  useEffect(() => {
    Crisp.configure('2cb38774-37f1-4c6f-9083-92d0837e8267', {
      autoload: false,
    });
    Crisp.setColorTheme(ChatboxColors.DeepPurple);
    Crisp.chat.onChatClosed(() => Crisp.chat.hide());
  }, []);

  const profileLink = isLawyer
    ? ROUTES.DASHBOARD.PROFILE
    : ROUTES.MARKETPLACE.PROFILE;

  const handleSwitch = async () => {
    if (!session) return;
    setIsLoading(true);
    await userService.updateUser(
      session.user.id,
      {
        role: isLawyer ? 'user' : 'lawyer',
      },
      session
    );
    handleLogout();
    router.refresh();
  };

  return (
    <div className='flex items-center gap-2'>
      {status === 'authenticated' && <NotificationDropdown />}

      <ThemeToggle />

      <Menubar className='h-10 w-10 cursor-pointer border-none bg-transparent p-0 shadow-none'>
        <MenubarMenu>
          <MenubarTrigger className='inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md p-0 hover:bg-muted'>
            <UserIcon className='h-6 w-6 text-purple-600 dark:text-foreground' />
          </MenubarTrigger>
          <MenubarContent>
            {status === 'authenticated' && (
              <>
                <Link href={profileLink}>
                  <MenubarItem className='cursor-pointer'>
                    <span className='flex items-center'>
                      <UserIcon className='mr-2 h-4 w-4' />
                      {t('navbar.myProfile')}
                    </span>
                  </MenubarItem>
                </Link>
                <MenubarSeparator />
              </>
            )}

            {status === 'authenticated' && isAdmin && (
              <>
                <MenubarItem className='cursor-pointer' onClick={handleSwitch}>
                  <span className='flex items-center'>
                    <ArrowLeftRight className='mr-2 h-4 w-4' />
                    Switch to {isLawyer ? 'User' : 'Lawyer'}
                  </span>
                </MenubarItem>
                <MenubarSeparator />

                <Link href={ROUTES.ADMIN}>
                  <MenubarItem className='cursor-pointer'>
                    <span className='flex items-center'>
                      <LayoutDashboard className='mr-2 h-4 w-4' />
                      {t('navbar.adminDashboard')}
                    </span>
                  </MenubarItem>
                </Link>
                <MenubarSeparator />
              </>
            )}

            <MenubarItem
              className='cursor-pointer'
              onClick={() => {
                Crisp.chat.open();
                Crisp.chat.show();
                Crisp.chat.onChatOpened(() => {
                  console.log('Chat opened');
                });
              }}
            >
              <span className='flex items-center'>
                <HelpCircle className='mr-2 h-4 w-4' />
                {t('navbar.help')}
              </span>
            </MenubarItem>

            <MenubarSeparator />

            <MenubarItem
              onClick={handleLanguageChange}
              className='cursor-pointer'
              disabled={isLoading || isPending}
            >
              <span className='flex items-center'>
                <Globe className='mr-2 h-4 w-4' />
                {isLoading || isPending
                  ? t('navbar.updating')
                  : `${currentLocale === 'en' ? 'Français' : 'English'}`}
              </span>
            </MenubarItem>

            <MenubarSeparator />

            <MenubarItem className='cursor-pointer'>
              {status === 'loading' ? (
                <div className='flex h-full w-full items-center text-start'>
                  <Settings className='mr-2 h-4 w-4 animate-spin' />
                  {t('navbar.updating')}
                </div>
              ) : status === 'authenticated' ? (
                <div
                  onClick={handleLogout}
                  className='flex h-full w-full items-center'
                >
                  <LogOut className='mr-2 h-4 w-4' />
                  {isLoading ? t('auth.login.loading') : t('navbar.logOut')}
                </div>
              ) : (
                <Link
                  href={ROUTES.AUTH.LOGIN}
                  className='flex h-full w-full items-center'
                  prefetch={true}
                >
                  <LogOut className='mr-2 h-4 w-4 rotate-180 transform' />
                  {t('navbar.login')}
                </Link>
              )}
            </MenubarItem>

            {status === 'unauthenticated' && (
              <>
                <MenubarSeparator />
                <Link href={ROUTES.AUTH.REGISTER} prefetch={true}>
                  <MenubarItem className='cursor-pointer'>
                    <span className='flex items-center'>
                      <UserIcon className='mr-2 h-4 w-4' />
                      {t('navbar.createAccount')}
                    </span>
                  </MenubarItem>
                </Link>
              </>
            )}
          </MenubarContent>
        </MenubarMenu>
      </Menubar>
    </div>
  );
}

function ClientI18nWrapper({ children }: { children: React.ReactNode }) {
  const locale = useCurrentLocale();
  return (
    <I18nProviderClient locale={locale} fallback='Loading...'>
      {children}
    </I18nProviderClient>
  );
}

export default function Controls() {
  return (
    <ClientI18nWrapper>
      <ControlsContent />
    </ClientI18nWrapper>
  );
}
